import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, primeCsrf } from './api';
import type { StripeCustomerData } from '@/types/api';

const KEY = (clientId: number) => ['stripe-customer', clientId] as const;

/** The Stripe customer currently linked to this client, or null. */
export function useStripeCustomer(clientId: number | undefined) {
  return useQuery({
    queryKey: clientId == null ? ['stripe-customer', null] : KEY(clientId),
    enabled: clientId != null,
    queryFn: async (): Promise<StripeCustomerData | null> => {
      const { data } = await api.get<{ customer: StripeCustomerData | null }>(
        `/v1/clients/${clientId}/stripe-customer`,
      );
      return data.customer;
    },
  });
}

/** Search Stripe for candidate customers to link (empty query = by client email). */
export function useSearchStripeCustomers(clientId: number, query: string, enabled: boolean) {
  return useQuery({
    queryKey: ['stripe-customer', clientId, 'search', query],
    enabled,
    queryFn: async (): Promise<StripeCustomerData[]> => {
      const { data } = await api.get<StripeCustomerData[]>(
        `/v1/clients/${clientId}/stripe-customer/search`,
        { params: query ? { q: query } : undefined },
      );
      return data;
    },
  });
}

export function useLinkStripeCustomer(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (customerId: string): Promise<StripeCustomerData> => {
      await primeCsrf();
      const { data } = await api.post<StripeCustomerData>(
        `/v1/clients/${clientId}/stripe-customer`,
        { customer_id: customerId },
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY(clientId) });
      qc.invalidateQueries({ queryKey: ['payment-methods', clientId] });
      qc.invalidateQueries({ queryKey: ['clients', clientId] });
    },
  });
}

/** Create (or adopt a same-email) Stripe customer for a client not yet linked. */
export function useEnsureStripeCustomer(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<StripeCustomerData> => {
      await primeCsrf();
      const { data } = await api.post<StripeCustomerData>(
        `/v1/clients/${clientId}/stripe-customer/ensure`,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY(clientId) });
      qc.invalidateQueries({ queryKey: ['payment-methods', clientId] });
      qc.invalidateQueries({ queryKey: ['clients', clientId] });
    },
  });
}

export function useUnlinkStripeCustomer(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<void> => {
      await primeCsrf();
      await api.delete(`/v1/clients/${clientId}/stripe-customer`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY(clientId) });
      qc.invalidateQueries({ queryKey: ['payment-methods', clientId] });
      qc.invalidateQueries({ queryKey: ['clients', clientId] });
    },
  });
}
